/**
 * Top-level UI variant configuration.
 *
 * The active variant is now chosen at *runtime* by the child via the
 * ThemePicker on the home page, and persisted in localStorage (see
 * src/hooks/useUIVariant.js). This file only defines the catalogue and
 * the default fallback used the first time the app loads.
 *
 * Available variants:
 *   'classic'  — original kid-friendly UI (light pastels)
 *   'cosmic'   — Lexonauts space theme (parallax starfield, planets,
 *                rocket progress, per-grade mascots)
 *   'summer'   — Greek summer / Cycladic palette + beach elements
 *
 * The page sets for each variant are wired in src/pages/pageRegistry.js.
 *
 * Δεν αλλάζουμε αυτό το αρχείο για να επιλέξουμε theme. Το UI θα επιλεγεί
 * από το παιδί απευθείας στην αρχική οθόνη.
 */

/** First-time visitor default. Picked once, then user choice wins. */
export const DEFAULT_UI_VARIANT = 'cosmic';

/** Catalogue of variants exposed to the picker. Order = display order. */
export const VARIANT_CATALOG = [
  {
    id: 'classic',
    icon: '✏️',
    palette: ['#2563eb', '#fbbf24', '#10b981'],
  },
  {
    id: 'cosmic',
    icon: '🚀',
    palette: ['#0b1226', '#2dd4bf', '#fcd34d'],
  },
  {
    id: 'summer',
    icon: '☀️',
    palette: ['#0ea5e9', '#fbbf24', '#f87171'],
  },
  {
    id: 'dino',
    icon: '🦖',
    palette: ['#22c55e', '#f59e0b', '#9a3412'],
  },
  {
    id: 'minoan',
    icon: '🏛️',
    palette: ['#0ea5e9', '#dc2626', '#fbbf24'],
  },
];

export const VARIANT_IDS = VARIANT_CATALOG.map((v) => v.id);

export function isKnownVariant(id) {
  return VARIANT_IDS.includes(id);
}

/**
 * Animation intensity tuner. Lowering this disables expensive decorative
 * loops for low-end devices or users who prefer reduced motion.
 *
 *   'full'     — all animations on
 *   'reduced'  — keep transitions, skip decorative loops
 *   'off'      — only essential transitions
 */
export const UI_MOTION = 'full';

/**
 * Home-screen layout preference. Pickable at runtime via the ThemeFab
 * popover (see src/components/theme/ThemeFab.jsx).
 *
 *   'stack'     — original vertical sequence: HomeAdvice, Quests + Pet
 *                 (2-col grid), full AvatarPicker beneath. Generous and
 *                 readable but scrolls on small screens.
 *   'dashboard' — compact 2×2 tile grid under the grade cards
 *                 (Quests / Pet / Avatar preview / Tip).
 *                 Less scroll, denser tiles, opens the full avatar
 *                 picker via a modal.
 */
export const DEFAULT_HOME_LAYOUT = 'stack';

export const HOME_LAYOUT_CATALOG = [
  { id: 'stack', icon: '🗒️' },
  { id: 'dashboard', icon: '🧩' },
];

export const HOME_LAYOUT_IDS = HOME_LAYOUT_CATALOG.map((l) => l.id);

export function isKnownHomeLayout(id) {
  return HOME_LAYOUT_IDS.includes(id);
}
